import { processStringSegments, StringSegment } from '../../../tools/processStringSegments';
import { ComplexValue } from '../types/ComplexValue';
import { Match } from '../types/Match';
import { Result } from '../types/Result';

const macrosRX = /([a-z]+(?:[._][a-z]+)*):(\s*)/gi;
const functionRX = /^([a-z]+(?:[._][a-z]+)*)\(([^)]+)\)/i;
const quotedRX = /^"[^"]+"/i;
const valueRX = /^\w+/i;

const escapeMap = {
  ' ': '&nbsp;',
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#039;',
};

const isInsideQuotes = (text: string, position: number): boolean => {
  let quoteCount = 0;

  for (let i = 0; i < position; i++) {
    if (text[i] === '"') {
      quoteCount++;
    }
  }

  return quoteCount % 2 === 1;
};

export const escapeChars = (text: string) => {
  return text
    .split('')
    .map((char) => escapeMap[char] || char)
    .join('');
};

const getValueInfo = (value: string) => {
  if (value.startsWith('"') && value.includes('"', 1)) {
    const matched = (value.match(quotedRX) || [''])[0];
    return {
      value: matched.slice(1, -1),
      operator: '',
      hasQuotes: true,
      hasBrackets: false,
      length: matched.length,
    };
  }

  const indexOfB1 = value.indexOf('(');
  if (indexOfB1 > -1 && value[indexOfB1 - 1] !== ' ') {
    const indexOfB2 = value.indexOf(')', indexOfB1 + 1);

    if (indexOfB2 > -1) {
      const withOp = value.match(functionRX) || ['', '', ''];
      if (withOp)
        return {
          value: withOp[2],
          operator: withOp[1],
          hasQuotes: false,
          hasBrackets: true,
          length: withOp[0].length,
        };
    }
  }

  const matched = value.match(valueRX) || [''];
  return {
    value: matched[0],
    operator: '',
    hasQuotes: false,
    hasBrackets: false,
    length: matched[0].length,
  };
};

export const formatAll = (
  text: string,
  currentIndex = -1,
  invalidFields: string[] = null,
): Result => {
  const matches: Match[] = [];
  const segments: StringSegment[] = [];

  const fakeMatch = Object.assign(['', '', ''], { index: text.length });
  const allMatches = Array.from(text.matchAll(macrosRX));

  // Filter out matches that are inside double quotes
  const matchedMacros = [
    ...allMatches.filter((match) => !isInsideQuotes(text, match.index)),
    fakeMatch,
  ];

  matchedMacros.forEach(
    (match: Partial<RegExpMatchArray>, matchIndex: number, arr: RegExpMatchArray[]) => {
      if (match === fakeMatch) return;

      const content: string[] = [];
      const segmentParts: string[] = [];

      const index = match.index;
      const token = match[1];
      const spaces = match[2];

      const nextStart = arr[matchIndex + 1]?.index || text.length;
      const textBetween = text.substring(index + match[0].length, nextStart);

      const valueInfo = getValueInfo(textBetween);
      const { hasBrackets, hasQuotes, operator, value } = valueInfo;

      if (token) {
        segmentParts.push(`${token}:`);
        const escToken = escapeChars(token);
        const className = `macro-input-token ${invalidFields?.includes(token) ? 'invalid' : ''}`;
        content.push(
          `<span class="${className}">${escToken}</span>`,
          '<span class="macro-input-operator">:</span>',
        );
      }

      if (spaces) {
        if (value) {
          segmentParts.push(spaces);
          content.push(spaces);
        } else {
          // 0-1 spaces = space between each segment
          // 1-X spaces = pointless spacing
          const space = spaces.length === 1 ? '' : spaces.slice(0, 1);
          segmentParts.push(space);
          content.push(space);
        }
      }

      if (value) {
        if (hasBrackets) {
          segmentParts.push(`${operator || ''}(`);
          const escOperator = escapeChars(operator || '');
          const html = `<span class="macro-input-operator">${escOperator}(</span>`;
          content.push(html);
        }

        const q = hasQuotes ? '"' : '';
        segmentParts.push(`${q}${value}${q}`);
        const escValue = escapeChars(value);
        const html = `<span class="macro-input-value">${q}${escValue}${q}</span>`;
        content.push(html);

        if (hasBrackets) {
          segmentParts.push(')');
          content.push(`<span class="macro-input-operator">)</span>`);
        }
      }

      const groupClasses = matchIndex === currentIndex ? 'is-focused' : '';
      const joined = content.join('');
      const formatted = `<span class="macro-input-group ${groupClasses}">${joined}</span>`;

      const segment = segmentParts.join('');
      const start = index;
      const end = start + segment.length;

      const prevSegment = segments[matchIndex - 1] || { end: -1 };
      if (prevSegment.end !== start - 1) {
        segments.push({
          start: prevSegment.end + 1,
          end: start - 1,
          process: escapeChars,
        });
      }

      segments.push({
        start: start,
        end: end,
        process: () => formatted,
      });

      matches.push({
        segment,
        token,
        operator,
        value,
        start,
        end,
        i: matchIndex,
      });
    },
  );

  if (segments.length) {
    // first segment was checked by the "prevSegment" above already
    const last = segments.at(-1);
    if (last.end !== text.length) {
      segments.push({
        start: last.end + 1,
        end: text.length,
        process: escapeChars,
      });
    }
  } else {
    segments.push({
      start: 0,
      end: text.length,
      process: escapeChars,
    });
  }

  const html = processStringSegments(text, segments);

  return { value: text, html, matches };
};

export const resultToComplex = ({ value, matches }: Result): ComplexValue => {
  const segments: (string | Match)[] = [];
  const macros: Match[] = [];
  const texts: string[] = [];
  let lastEnd = 0;

  const addText = (match?: Match) => {
    const text = value.slice(lastEnd, match?.start).trim();
    if (!text) return;
    segments.push(text);
    texts.push(text);
  };

  for (const match of matches) {
    if (lastEnd !== match.start) {
      addText(match);
    }
    segments.push(match);
    macros.push(match);
    lastEnd = match.end;
  }

  if (lastEnd < value.length) {
    addText();
  }

  return {
    segments,
    macros,
    texts,
    text: value,
    current: '',
  };
};
